// web/src/tutorial/detail/zheng-drift.ts
// ZhengDrift · 软件界面详细说明（Rust 端逐个页签、逐个参数）
// 依据：G:\zheng_drift_rust
//   src/ui/app.rs（四个页签与顺序）、avatar_panel.rs（角色选择 / 动作参数 / 记录池设置 /
//   技法检测 / 演奏技法）、avatar_display.rs（角色信息与编辑表单）、midi_panel.rs（MIDI 文件管理 /
//   参数设置 / 轨道选择）、technique_editor.rs（技法编辑器）、execute_panel.rs（配置概览 / 执行操作 /
//   控制台输出）、components.rs（卡片与按钮形态）、data.rs（取值范围与默认值）
//   界面原文：assets/i18n/zh-CN.json 与 assets/i18n/en-US.json
// 截图：web/assets/image/products/zheng-drift/{CN,EN}/*.png（4 张，822×852）

import { l } from "../l";
import type { TutorialDoc } from "../types";

export const zhengDriftDetail: TutorialDoc = {
  key: "zheng_drift",
  slug: "zheng-drift",
  kind: "detail",
  icon: "🪕",
  title: l("ZhengDrift 软件界面详细说明", "ZhengDrift UI Reference"),
  subtitle: l(
    "按页签从上到下拆开讲：每个分区负责什么、每个按钮点下去会发生什么、每个滑块调大或调小会有什么变化。想先跑通一遍，看[快速上手](/quickstart/zheng-drift.html)。",
    "A tab-by-tab walkthrough: what each card is for, what every button does when pressed, and what changes when you move each slider. To get a first run done, see the [Quick Start](/quickstart/zheng-drift.html).",
  ),
  readMinutes: 18,
  video: {
    hidden: true,
    caption: l("本页不内嵌视频", "No embedded video on this page"),
  },
  images: [
    {
      src: "/assets/image/products/zheng-drift/CN/参数设置.png",
      srcEn: "/assets/image/products/zheng-drift/EN/Parameters.png",
      label: l("参数设置", "Parameters"),
      width: 822,
      height: 852,
    },
    {
      src: "/assets/image/products/zheng-drift/CN/MIDI信息.png",
      srcEn: "/assets/image/products/zheng-drift/EN/MIDI.png",
      label: l("MIDI 信息", "MIDI"),
      width: 822,
      height: 852,
    },
    {
      src: "/assets/image/products/zheng-drift/CN/技法编辑.png",
      srcEn: "/assets/image/products/zheng-drift/EN/Technique Editor.png",
      label: l("技法编辑", "Technique"),
      width: 822,
      height: 852,
    },
    {
      src: "/assets/image/products/zheng-drift/CN/生成动画.png",
      srcEn: "/assets/image/products/zheng-drift/EN/Animation.png",
      label: l("生成动画", "Animation"),
      width: 822,
      height: 852,
    },
  ],
  sections: [
    // ─────────────────────────────────────────────────────────────
    {
      id: "how-to-read",
      title: l("这篇怎么用", "How to use this page"),
      blocks: [
        {
          kind: "p",
          text: l(
            "本页是**参数手册**：把软件界面按顶栏页签顺序（参数设置 → MIDI 信息 → 技法编辑 → 生成动画）逐屏拆开，每个滑块、每个输入框、每个按钮都讲到。滚到哪一节，右下角就自动换成那一屏的截图。",
            "This page is a **parameter reference**: it walks the UI in the title-bar order (参数设置 / Parameters → MIDI 信息 / MIDI → 技法编辑 / Technique → 生成动画 / Animation), one screen at a time, covering every slider, field and button. The floating screenshot follows the section you scroll to.",
          ),
        },
        {
          kind: "p",
          text: l(
            "只想先把流程跑通，去[快速上手](/quickstart/zheng-drift.html)：那里的三步（选演奏者 → 选 MIDI → 生成动画）全部用默认参数，不需要读这一页。",
            "If you just want a first run, read the [Quick Start](/quickstart/zheng-drift.html): its three steps (pick a performer → pick a MIDI → generate) use default parameters throughout, so none of this page is required.",
          ),
        },
        {
          kind: "callout",
          tone: "tip",
          text: l(
            "参数改动**不会自动保存**，重开软件回到默认值。所以调出满意的一套之后，建议把用到的数值记在曲子的备注里，下次照抄。",
            "Parameter changes are **not saved** — restarting the app restores the defaults. Once you find a setup you like, note the numbers down next to the piece and re-enter them next time.",
          ),
        },
        {
          kind: "note",
          text: l(
            "界面上所有紫色圆角小方块（01、02…）是分区编号，用来在截图和正文之间对位。注意角色信息卡片与技法检测卡片都编号为 02，这是程序本身的编号，不是笔误。",
            "The small purple badges (01, 02, …) number the cards so the text and the screenshots line up. Note that the avatar-info card and the technique-detection card are both numbered 02 — that is the app's own numbering, not a typo.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "tab-parameters",
      title: l("参数设置页", "The Parameters tab"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "参数设置 页签管三件事：**谁在弹**（选演奏者、管理演奏者列表）、**手怎么动**（动作参数、记录池）、**哪些音算技法**（技法检测、演奏技法）。它同时是主流程的第 1 步。",
            "The 参数设置 / Parameters tab covers three things: **who plays** (choosing and managing performers), **how the hands move** (motion parameters and the recorder pool), and **which notes count as techniques** (technique detection and performance techniques). It is also step 1 of the main flow.",
          ),
        },
        {
          kind: "p",
          text: l(
            "版面是左右两栏加底部两张通栏卡片：左栏 01 角色选择 与 03 动作参数，右栏 02 角色信息 与 04 记录池设置，底部是 02 技法检测 与 05 演奏技法。",
            "The layout is two columns with two full-width cards underneath: 01 角色选择 / Avatar Selection and 03 动作参数 / Motion Parameters on the left, 02 角色信息 / Avatar Info and 04 记录池设置 / Recorder Pool on the right, and 02 技法检测 / Technique Detection plus 05 演奏技法 / Performance Techniques across the bottom.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l(
            "第一次可以整屏不动",
            "Skip the whole screen on a first run",
          ),
          text: l(
            "除了下拉菜单里选一个人，这一屏其余控件第一次都可以原样不动。默认值就是按「跑得通、看起来像人在弹」调的。",
            "Apart from picking someone in the dropdown, everything on this screen can stay untouched the first time — the defaults are tuned to run and to look like a person playing.",
          ),
        },
      ],
      subs: [
        {
          id: "tab-parameters-avatar",
          title: l(
            "01 角色选择与 02 角色信息",
            "01 Avatar Selection and 02 Avatar Info",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "01 角色选择 卡片里只有一个 未选择 下拉菜单（选过之后显示当前演奏者名字）。展开就是软件自带的演奏者列表；点一下即生效，同时会把该演奏者的 .zheng_master 文件写进当前配置。",
                "The 01 角色选择 / Avatar Selection card holds a single 未选择 / Not selected dropdown (it shows the current performer's name once you pick one). Opening it lists the bundled performers; clicking one takes effect immediately and stores that performer's .zheng_master file as the current selection.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "选中之后，右栏 02 角色信息 会显示该演奏者的人物图片（最长边被限制在 300×250 内等比缩放）。",
                  "Once selected, the 02 角色信息 / Avatar Info card on the right shows that performer's portrait (scaled to fit within 300×250, aspect preserved).",
                ),
                l(
                  '演奏者列表为空时，左栏显示 暂无角色 和一个居中的 ➕ 新增角色 按钮；右栏显示 暂无角色 与「请在左侧点击"新增角色"按钮创建第一个角色」。',
                  'With an empty list the left card shows 暂无角色 / No avatars yet and a centred ➕ 新增角色 / ➕ New Avatar button, while the right one shows 暂无角色 and 请在左侧点击"新增角色"按钮创建第一个角色 / Click the "New Avatar" button on the left to create the first avatar.',
                ),
                l(
                  "演奏者只在**生成动画**阶段参与计算：它提供骨骼与记录器，决定手、手指、筝体与琴弦落点。指法本身（哪根手指拨哪根弦）由弦映射与束搜索算出来，与选谁无关。所以**换演奏者不需要重跑指法**。",
                  "The performer only takes part in the **animation** stage: it supplies the bones and recorders that place hands, fingers, zheng body and strings in space. The fingering itself (which finger plucks which string) comes from the string mapping and beam search and is independent of the performer — so **switching performer does not require regenerating the fingering**.",
                ),
              ],
            },
            {
              kind: "table",
              head: [
                l("按钮", "Button"),
                l("点下去会发生什么", "What it does"),
              ],
              rows: [
                [
                  l("✏ 修改", "✏ 修改 / ✏ Edit"),
                  l(
                    "把 02 角色信息 卡片就地换成编辑表单，并预先填好当前演奏者的名称、图片、配置文件三项。改完 💾 保存 才写回磁盘。",
                    "Swaps the 02 角色信息 / Avatar Info card for an edit form pre-filled with the current performer's name, image and config file. Nothing is written to disk until you click 💾 保存 / 💾 Save.",
                  ),
                ],
                [
                  l("➕ 新增", "➕ 新增 / ➕ Add"),
                  l(
                    "同样换成表单，但三个字段是空的，用来登记一位新演奏者。",
                    "Opens the same form with empty fields, for registering a new performer.",
                  ),
                ],
                [
                  l("🗑 删除", "🗑 删除 / 🗑 Delete"),
                  l(
                    "弹出 确认删除 窗口，写明「此操作不可撤销。」，需要再点 ✅ 确认删除 才真正删除；也可以 ❌ 取消。删除最后一个角色会被拒绝。",
                    "Opens a 确认删除 / Confirm Delete window stating 此操作不可撤销。 / This action cannot be undone. and only deletes after you click ✅ 确认删除 / ✅ Confirm Delete (❌ 取消 / ❌ Cancel backs out). Deleting the last remaining performer is refused.",
                  ),
                ],
              ],
            },
            {
              kind: "table",
              head: [
                l("表单字段", "Form field"),
                l("说明", "Notes"),
                l("怎么填", "How to fill it"),
              ],
              rows: [
                [
                  l("角色名称:", "角色名称: / Name:"),
                  l(
                    "演奏者在列表和输出文件名里显示的名字，不能为空、也不能与已有名字重复。",
                    "The name shown in the list and used in output file names. Must be non-empty and unique.",
                  ),
                  l("直接输入", "Type it in"),
                ],
                [
                  l("角色图片:", "角色图片: / Image:"),
                  l(
                    "列表和 02 角色信息 里显示的人物图片，支持 .png / .jpg / .jpeg。",
                    "The portrait shown in the list and in 02 角色信息 / Avatar Info; accepts .png / .jpg / .jpeg.",
                  ),
                  l("点 📂 选择图片", "Click 📂 选择图片 / 📂 Choose Image"),
                ],
                [
                  l("配置文件:", "配置文件: / Config file:"),
                  l(
                    "该演奏者对应的 .zheng_master 骨骼 / 记录器数据，**必填**，没有它生成动画会失败。",
                    "The performer's .zheng_master bone / recorder data. **Required** — without it the animation stage fails.",
                  ),
                  l(
                    "点 📂 选择配置文件",
                    "Click 📂 选择配置文件 / 📂 Choose Config File",
                  ),
                ],
              ],
            },
            {
              kind: "p",
              text: l(
                "💾 保存 时程序会把图片复制到程序的 assets/image/、把 .zheng_master 复制到 assets/zheng_master/，然后重写角色列表文件，所以下次启动这个演奏者还在。表单下方出现红色横幅时是校验没过，文案形如「错误: 角色名字不能为空」「错误: 角色名字已存在」「错误: 必须选择角色配置文件(.zheng_master)」。",
                "On 💾 保存 / 💾 Save the app copies the image into its own assets/image/, the .zheng_master into assets/zheng_master/, then rewrites the performer list — so the performer survives a restart. A red banner under the form means validation failed, reading 错误: 角色名字不能为空 / Error: Avatar name cannot be empty, 错误: 角色名字已存在 / Error: Avatar name already exists or 错误: 必须选择角色配置文件(.zheng_master) / Error: an avatar config file (.zheng_master) must be selected.",
              ),
            },
            {
              kind: "note",
              text: l(
                "✏ 修改 / ➕ 新增 / 🗑 删除 和表单都属于「管理演奏者」，与生成动画无关；只想换个人弹，用左栏下拉菜单就够。",
                "✏ 修改 / ✏ Edit / ➕ 新增 / ➕ Add / 🗑 删除 / 🗑 Delete and the form are all about managing the performer roster and are unrelated to generating. To simply change who plays, the left-hand dropdown is enough.",
              ),
            },
          ],
        },
        {
          id: "tab-parameters-motion",
          title: l("03 动作参数", "03 Motion Parameters"),
          blocks: [
            {
              kind: "p",
              text: l(
                "这一卡片是整屏最值得动的地方：八个滑块全部直接决定手的观感。滑块可以直接拖，也可以点右侧的数字框输入精确值。",
                "This is the card most worth touching: all eight sliders directly shape how the hands look. Drag a slider, or click the number box on its right to type an exact value.",
              ),
            },
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("管什么", "What it controls"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("怎么调", "How to tune it"),
              ],
              rows: [
                [
                  l("按下耗时 (秒):", "按下耗时 (秒): / Press duration (sec):"),
                  l(
                    "手指从准备到触弦花多久，也就是「落指快慢」。调小更利落、更有颗粒感；调大更柔、更像慢板。",
                    "How long a finger takes to travel from ready to touching the string — how snappy the attack looks. Smaller is crisper and more percussive; larger is softer and suits slow passages.",
                  ),
                  l("0.01 ~ 1.0", "0.01 – 1.0"),
                  l("0.0875", "0.0875"),
                  l(
                    "曲子快速密集（十六分音符跑动）时适当调小；抒情慢曲可以加到 0.15 左右。",
                    "Lower it for dense fast passagework; for lyrical slow pieces 0.15 is a reasonable bump.",
                  ),
                ],
                [
                  l("抬起耗时 (秒):", "抬起耗时 (秒): / Lift duration (sec):"),
                  l(
                    "手指从触弦回到保持 / 释放花多久，也就是「离弦快慢」。它和按下耗时是两段独立时长，默认抬起比按下略慢，手看起来更沉稳。",
                    "How long a finger takes to go from touching back to hold / release — how fast it leaves the string. Press and lift are independent; by default lift is slightly slower, which reads as a steadier hand.",
                  ),
                  l("0.01 ~ 1.0", "0.01 – 1.0"),
                  l("0.12", "0.12"),
                  l(
                    "想让动作更「弹」一点，把它压到 0.08 以下；觉得手一直在抖，就往上加。",
                    "To make the motion springier, push it below 0.08; if the hand looks jittery, raise it.",
                  ),
                ],
                [
                  l(
                    "移动速度 (弦/秒):",
                    "移动速度 (弦/秒): / Move speed (strings/s):",
                  ),
                  l(
                    "手掌沿弦列横向移动的速度上限。它决定「这个音来得及挪过去吗」——来不及的音会在指法阶段被剔除。",
                    "The upper bound on how fast the palm travels sideways along the strings. It decides whether a hand can physically reach the next note in time; notes it cannot reach are dropped during fingering.",
                  ),
                  l("1.0 ~ 100.0", "1.0 – 100.0"),
                  l("20.0", "20.0"),
                  l(
                    "控制台出现大量「因手掌移动速度超标而跳过」时调大（30~50 很常见）；想要更克制的横向走位就调小。这项改了要重跑 🎼 2. 生成指法。",
                    "Raise it (30–50 is common) when the console reports many frames skipped for excessive palm speed; lower it for more restrained lateral motion. Changing it means re-running 🎼 2. 生成指法 / 🎼 2. Generate Fingering.",
                  ),
                ],
                [
                  l(
                    "最大过渡时长 (秒):",
                    "最大过渡时长 (秒): / Max transition duration (sec):",
                  ),
                  l(
                    "一次换位最多能从「保持」里借多少时间给抬手过渡。借得越多，走位越从容，但音与音之间的衔接会被拉长。",
                    "How much time a transition may borrow from the preceding hold. More borrowing makes the travel look unhurried, at the cost of a longer gap between notes.",
                  ),
                  l("0.5 ~ 10.0", "0.5 – 10.0"),
                  l("3.00", "3.00"),
                  l(
                    "大跨度跳弦多的曲子可以加到 4~5；键盘感太松散就压回 1~2。",
                    "For pieces with many wide string jumps 4–5 helps; if the motion feels loose, bring it back to 1–2.",
                  ),
                ],
                [
                  l("抬掌幅度:", "抬掌幅度: / Hand lift amplitude:"),
                  l(
                    "过渡帧里手掌抬高多少的倍率。1.00 是标准高度，0 表示换位时手掌完全不抬起、直接贴着弦列平移。",
                    "A multiplier on how high the palm lifts during transitions. 1.00 is the standard lift; 0 means the palm never rises and simply slides along the strings.",
                  ),
                  l("0.0 ~ 2.0", "0.0 – 2.0"),
                  l("1.00", "1.00"),
                  l(
                    "看着像在弦上蹭，就往上加到 1.3~1.6；觉得手挥得太夸张，压到 0.5 以下。",
                    "If the hand looks like it is scraping the strings, raise it to 1.3–1.6; if it waves theatrically, drop below 0.5.",
                  ),
                ],
                [
                  l("外拨概率 (0-1):", "外拨概率 (0-1): / Outward pluck:"),
                  l(
                    "拨弦方向随机取「向外」的概率。0 表示全部内拨（朝手心），值越大越多手指朝外拨，整段演奏的手指走向越不整齐但也越随机。",
                    "The chance that a pluck is directed outward instead of inward. 0 means every pluck goes inward (toward the palm); larger values send more fingers outward, making the finger directions less uniform and more random.",
                  ),
                  l("0.0 ~ 1.0", "0.0 – 1.0"),
                  l("0.000", "0.000"),
                  l(
                    "默认 0 是「最整齐」的选项，通常不用动；想要更手忙脚乱的真实感可以试 0.1~0.3。改了要重跑 🎼 2. 生成指法。",
                    "The default 0 is the tidiest option and usually stays put; try 0.1–0.3 for a busier, more human look. Changing it means re-running 🎼 2. 生成指法 / 🎼 2. Generate Fingering.",
                  ),
                ],
                [
                  l("朝向敏感度:", "朝向敏感度: / Orientation sensitivity:"),
                  l(
                    "人物上身朝向（转身 / 转胸 / 转头）对双手位置变化的反应门槛。值越小越容易转身，越大越「懒得动」。",
                    "The threshold at which the upper body (body / chest / head turn) reacts to the hands moving. Lower turns more readily; higher is more reluctant.",
                  ),
                  l("0.0 ~ 1.0", "0.0 – 1.0"),
                  l("0.850", "0.850"),
                  l(
                    "觉得人物一直僵着不动就降到 0.6 左右；觉得上身晃得晕就升到 0.9 以上。",
                    "If the character never turns, lower it toward 0.6; if the upper body sways distractingly, raise it above 0.9.",
                  ),
                ],
                [
                  l(
                    "手位变化阈值:",
                    "手位变化阈值: / Hand position threshold:",
                  ),
                  l(
                    "手掌位移片段的切分阈值，单位是「弦」。超过这个位移才算一次真正的手位变化，否则并进前一段。",
                    "The split threshold for palm-travel segments, measured in strings. A move smaller than this is folded into the previous segment instead of counting as a new hand position.",
                  ),
                  l("0.5 ~ 5.0", "0.5 – 5.0"),
                  l("2.00", "2.00"),
                  l(
                    "调小会让手位切换更频繁、动作更细碎；调大则走位更「成块」。默认 2.00 一般不碰。",
                    "Smaller values switch hand positions more often and chop the motion up; larger values make travel blockier. The default 2.00 is rarely worth touching.",
                  ),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "tip",
              title: l("调参的推荐顺序", "A sane order for tuning"),
              text: l(
                "先只动 移动速度 (弦/秒):，让控制台不再大量跳帧；再调 按下耗时 (秒): / 抬起耗时 (秒): 找手感；最后才碰 抬掌幅度: 与 朝向敏感度:。一次只改一项，跑完看一眼再改下一项。",
                "Start with 移动速度 (弦/秒): / Move speed (strings/s): alone until the console stops reporting skipped frames; then shape 按下耗时 (秒): / Press duration (sec): / 抬起耗时 (秒): / Lift duration (sec):; only then touch 抬掌幅度: / Hand lift amplitude: and 朝向敏感度: / Orientation sensitivity:. Change one value at a time and watch a run before the next.",
              ),
            },
            {
              kind: "note",
              text: l(
                "按下耗时 (秒): / 抬起耗时 (秒): / 抬掌幅度: 只影响**动画阶段**，改完直接重跑 🎹 3. 生成动画 就行；移动速度 (弦/秒): / 外拨概率 (0-1): / 手位变化阈值: 参与指法计算，要从 🎼 2. 生成指法 起跑。",
                "按下耗时 (秒): / Press duration (sec): / 抬起耗时 (秒): / Lift duration (sec): / 抬掌幅度: / Hand lift amplitude: affect only the **animation** stage, so re-run 🎹 3. 生成动画 / 🎹 3. Generate Animation. 移动速度 (弦/秒): / Move speed (strings/s): / 外拨概率 (0-1): / Outward pluck: / 手位变化阈值: / Hand position threshold: feed the fingering computation, so restart from 🎼 2. 生成指法 / 🎼 2. Generate Fingering.",
              ),
            },
          ],
        },
        {
          id: "tab-parameters-pool",
          title: l("04 记录池设置", "04 Recorder Pool"),
          blocks: [
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("管什么", "What it controls"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("怎么调", "How to tune it"),
              ],
              rows: [
                [
                  l("记录池大小:", "记录池大小: / Pool size:"),
                  l(
                    "生成指法时同时保留多少条候选演奏记录（束搜索宽度）。池子越大，越可能找到整体更顺的指法，代价是耗时与内存成倍上涨。",
                    "How many candidate performance records the fingering search keeps alive at once (the beam width). A larger pool is more likely to find a globally smoother fingering, at a multiplied cost in time and memory.",
                  ),
                  l("10 ~ 600（取整）", "10 – 600 (rounded)"),
                  l("200", "200"),
                  l(
                    "某几处指法明显别扭时加到 400~600 再跑一遍对比；急着出结果就降到 100 以下，代价是个别段落可能不够顺。改了要重跑 🎼 2. 生成指法。",
                    "When a few passages come out awkward, raise it to 400–600 and compare; when you are in a hurry, drop below 100 and accept the occasional rough passage. Changing it means re-running 🎼 2. 生成指法 / 🎼 2. Generate Fingering.",
                  ),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "记录池大小: 是这一屏唯一明显影响**生成耗时**的参数。日志里的「指法生成进度：(x/y) - 速度：N steps/s」会随池子变大而变慢。",
                "记录池大小: / Pool size: is the one control on this screen that visibly affects **how long generation takes**. The 指法生成进度：(x/y) - 速度：N steps/s / Fingering progress: (x/y) - speed: N steps/s log line slows down as the pool grows.",
              ),
            },
          ],
        },
        {
          id: "tab-parameters-technique",
          title: l(
            "02 技法检测与 05 演奏技法",
            "02 Technique Detection and 05 Performance Techniques",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "底部两张卡片决定「哪些音符会被当成摇指 / 琶音」。摇指（同一根弦被快速反复拨动）与琶音（一串音符顺着弦依次扫过）在古筝演奏里是特殊手法，程序识别出来后会按技法生成动作，而不是逐音处理。",
                "The two cards at the bottom decide **which notes are treated as tremolo or arpeggio**. Both are signature guzheng techniques — a tremolo is one string plucked rapidly over and over, an arpeggio is a run swept across adjacent strings — and once detected the app animates them as techniques rather than note by note.",
              ),
            },
            {
              kind: "table",
              head: [
                l("开关 / 参数", "Switch / parameter"),
                l("勾上（或调大）会怎样", "What happens when on / higher"),
                l("默认", "Default"),
                l("什么时候改它", "When to change it"),
              ],
              rows: [
                [
                  l("启用摇指检测", "启用摇指检测 / Enable tremolo detection"),
                  l(
                    "开启后，快速反复拨同一根弦的片段会被聚合为摇指，用连续摇动的手型演奏。关掉则每个音都按普通音符逐个演奏。",
                    "With it on, rapid repeats on the same string are grouped into a tremolo and played with a continuous rocking motion. With it off, every note is played as an ordinary note.",
                  ),
                  l("勾选", "On"),
                  l(
                    "曲子里本来就没有摇指、却总被误判成摇指时关掉，能省掉去 技法编辑 逐条撤销的麻烦。",
                    "Turn it off when a piece has no real tremolo but keeps getting false positives — it saves undoing them one by one on 技法编辑 / Technique.",
                  ),
                ],
                [
                  l("启用琶音检测", "启用琶音检测 / Enable arpeggio detection"),
                  l(
                    "开启后，顺着弦列依次扫过的音符串会被识别为琶音，并按上行 / 下行方向演奏。关掉则拆成单音逐个弹。",
                    "With it on, runs that sweep across adjacent strings are recognised as arpeggios and played in their ascending / descending direction. With it off they are split into individual notes.",
                  ),
                  l("勾选", "On"),
                  l(
                    "分解和弦很多、但你想让它听起来是逐音而不是扫弦时关掉。",
                    "Turn it off when the piece is full of broken chords but you want them picked note by note rather than swept.",
                  ),
                ],
                [
                  l(
                    "启用手掌移动速度检查",
                    "启用手掌移动速度检查 / Enable hand speed check",
                  ),
                  l(
                    "勾上时，来不及移动过去的音符会被剔除（宁缺毋滥，动作更自然）；旁边的提示就是「(关闭后不跳过任何音符)」，关掉后所有音符都保留，代价是可能出现手掌瞬移。",
                    "When on, notes the palm cannot reach in time are dropped (fewer notes, more natural motion); the hint beside it reads (关闭后不跳过任何音符) / (when off, no notes are skipped). When off, every note is kept, at the risk of the palm teleporting.",
                  ),
                  l("勾选", "On"),
                  l(
                    "出现「因手掌移动速度超标而跳过」且你不能接受漏音时：先试着调大 移动速度 (弦/秒):；确实压不下来，再考虑关掉这一项并接受稍不自然的动作。",
                    "When you see skipped frames for excessive palm speed and cannot accept dropped notes: first raise 移动速度 (弦/秒): / Move speed (strings/s):; only if that fails, turn this off and accept slightly unnatural motion.",
                  ),
                ],
                [
                  l(
                    "摇指检测最大间隔值 (秒):",
                    "摇指检测最大间隔值 (秒): / Tremolo max interval (sec):",
                  ),
                  l(
                    "两次重复拨弦之间最长允许隔多久仍算同一段摇指。调大 → 更容易把松散的重复音并成一段摇指；调小 → 只有非常密集的重复才算摇指。",
                    "The longest gap between repeated plucks that still counts as the same tremolo. Larger groups looser repeats into one tremolo; smaller requires genuinely dense repetition.",
                  ),
                  l("0.0 ~ 1.0 / 默认 0.3", "0.0 – 1.0 / default 0.3"),
                  l(
                    "该识别成摇指的地方没识别出来，往上加一点（0.4~0.5）；不该识别的地方被合并了，往下减。",
                    "Raise it a little (0.4–0.5) when real tremolos are missed; lower it when unrelated repeats get merged.",
                  ),
                ],
                [
                  l(
                    "琶音检测最大间隔值 (秒):",
                    "琶音检测最大间隔值 (秒): / Arpeggio max interval (sec):",
                  ),
                  l(
                    "琶音里相邻两音之间最长允许隔多久。调大 → 慢速分解和弦也会被当作琶音扫过去；调小 → 只有快速扫弦才算。",
                    "The longest gap between adjacent notes of an arpeggio. Larger sweeps even slow broken chords; smaller only counts fast sweeps.",
                  ),
                  l("0.0 ~ 1.0 / 默认 0.2", "0.0 – 1.0 / default 0.2"),
                  l(
                    "慢速分解和弦被扫成一片、听起来糊，就调小；快速琶音被拆成单音，就调大。",
                    "Lower it when slow broken chords get swept into a blur; raise it when fast arpeggios are being split into single notes.",
                  ),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "warn",
              title: l(
                "改完要重跑「解析乐曲」",
                "Re-run parsing after changing these",
              ),
              text: l(
                "这五个控件都在**音符处理阶段**起作用。改过任何一个，都必须从 🎵 1. 解析乐曲 重跑，否则 技法编辑 里看到的还是上一版的识别结果。",
                "All five controls act during the **note-processing stage**. After changing any of them you must re-run from 🎵 1. 解析乐曲 / 🎵 1. Parse Music, otherwise 技法编辑 / Technique still shows the previous detection result.",
              ),
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "tab-midi",
      title: l("MIDI 信息页", "The MIDI tab"),
      image: { image: 1 },
      blocks: [
        {
          kind: "p",
          text: l(
            "MIDI 信息 页签负责「读哪一份 MIDI、读其中哪些轨道」。左栏是 01 MIDI 文件管理 与 02 参数设置，右栏是 03 轨道选择。这是主流程的第 2 步。",
            "The MIDI 信息 / MIDI tab decides **which MIDI file and which of its tracks** to read. The left column holds 01 MIDI 文件管理 / MIDI File Management and 02 参数设置 / Parameters; the right column holds 03 轨道选择 / Track Selection. This is step 2 of the main flow.",
          ),
        },
        {
          kind: "note",
          text: l(
            "这一屏的改动会影响下游所有环节：换文件、换通道、换轨道之后，output/ 里的中间文件名就对不上了，必须从 🎵 1. 解析乐曲 重新开始。",
            "Changes here ripple downstream: after switching file, channel or tracks the intermediate file names in output/ no longer match, so you must restart from 🎵 1. 解析乐曲 / 🎵 1. Parse Music.",
          ),
        },
      ],
      subs: [
        {
          id: "tab-midi-file",
          title: l("01 MIDI 文件管理", "01 MIDI File Management"),
          blocks: [
            {
              kind: "ul",
              items: [
                l(
                  "当前文件: 回显已选文件名；没选时这里显示 未选择 MIDI 文件 与「建议先选择一个 .mid 或 .midi 文件，再扫描轨道信息。」",
                  "当前文件: / Current file: echoes the chosen file name; with nothing loaded it shows 未选择 MIDI 文件 / No MIDI file selected and 建议先选择一个 .mid 或 .midi 文件，再扫描轨道信息。 / Select a .mid or .midi file first, then scan the track info.",
                ),
                l(
                  "📂 选择 MIDI 文件 打开系统文件对话框，过滤器只放行 .mid / .midi。选了别的扩展名会报「错误：文件扩展名不正确，期望.mid 或.midi，实际为.xxx」。",
                  "📂 选择 MIDI 文件 / 📂 Select MIDI File opens the system file dialog filtered to .mid / .midi. Any other extension triggers 错误：文件扩展名不正确，期望.mid 或.midi，实际为.xxx / Error: incorrect file extension; expected .mid or .midi, got .xxx.",
                ),
                l(
                  "🔍 扫描 MIDI 信息 读取文件的轨道清单，只列出**含音符**的轨道，填满右栏 03 轨道选择 的表格。没选文件就点它会提示「错误：请先选择 MIDI 文件」。",
                  "🔍 扫描 MIDI 信息 / 🔍 Scan MIDI Info reads the track list, keeps only tracks that **contain notes**, and fills the 03 轨道选择 / Track Selection table. Pressing it with no file loaded reports 错误：请先选择 MIDI 文件 / Error: please select a MIDI file first.",
                ),
                l(
                  "下方虚线框 📥 将 MIDI 文件拖放到此处 是拖放区，提示「支持 .mid 和 .midi 格式」。把文件从资源管理器拖进窗口即刻生效，等于选择文件那一步。",
                  "The dashed 📥 将 MIDI 文件拖放到此处 / 📥 Drop a MIDI file here box below is a drop zone, hinting 支持 .mid 和 .midi 格式 / Supports .mid and .midi formats. Dragging a file in from the file manager takes effect immediately and replaces the file-picking step.",
                ),
              ],
            },
            {
              kind: "callout",
              tone: "tip",
              text: l(
                "扫描是**只读**操作，随便点不会改你的文件。真正会写文件的是右栏的 ✂️ 修剪 MIDI 音符。",
                "Scanning is **read-only** — press it as often as you like. The only control here that writes a file is ✂️ 修剪 MIDI 音符 / ✂️ Trim MIDI Notes on the right.",
              ),
            },
          ],
        },
        {
          id: "tab-midi-params",
          title: l("02 参数设置", "02 Parameter Settings"),
          blocks: [
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("管什么", "What it controls"),
                l("取值", "Values"),
                l("默认", "Default"),
                l("怎么调", "How to tune it"),
              ],
              rows: [
                [
                  l(
                    "使用通道 (-1=所有):",
                    "使用通道 (-1=所有): / Use channel (-1=all):",
                  ),
                  l(
                    "只看某一个 MIDI 通道的音符。多轨文件里常有人把主旋律放在某个固定通道上，靠它一次筛干净。-1 表示不过滤。",
                    "Restricts reading to a single MIDI channel. Multi-track files often keep the melody on one channel, so this filters in one move. -1 means no filtering.",
                  ),
                  l("整数，-1 = 所有", "Integer, -1 = all"),
                  l("-1", "-1"),
                  l(
                    "改了会**自动重新扫描**轨道，不用手点扫描按钮。只想弹某个声部时填它的通道号。",
                    "Changing it **re-scans automatically** — no need to press scan. Fill in the channel number when you only want one voice.",
                  ),
                ],
                [
                  l("FPS:", "FPS:"),
                  l(
                    "生成动画的帧率，也是日志里「在 N FPS 下，合计 M 帧」的换算依据。它直接决定输出动画的时间精度与文件大小。",
                    "The frame rate of the generated animation, and the basis for the 在 N FPS 下，合计 M 帧 / At N FPS, M frames in total line in the log. It sets the time resolution and file size of the output.",
                  ),
                  l("数值，可拖动或输入", "Numeric, drag or type"),
                  l("60", "60"),
                  l(
                    "和 Blender / Unreal 场景的帧率保持一致（常见 30 / 60）；不一致的话播放速度会对不上。改完同样会自动重新扫描。",
                    "Keep it identical to your Blender / Unreal scene frame rate (30 / 60 are common); a mismatch makes playback drift. Changing it also re-scans automatically.",
                  ),
                ],
                [
                  l("音符量化:", "音符量化: / Note quantization:"),
                  l(
                    "把音符起始时间对齐到网格，消除演奏录制带来的微小时间抖动。可选 无量化 / 1/16 / 1/32 / 1/64 / 1/128，分母越大网格越细。",
                    "Snaps note start times to a grid, removing the small timing jitter of recorded performance. Options are 无量化 / None / 1/16 / 1/32 / 1/64 / 1/128; a larger denominator means a finer grid.",
                  ),
                  l("五种档位", "Five steps"),
                  l("1/16", "1/16"),
                  l(
                    "MIDI 是人工对齐过的就用 无量化，保留原表情；用键盘随手录的、节奏发飘就选 1/16 或更细。",
                    "Use 无量化 / None for already-quantised MIDI to keep the original expression; pick 1/16 or finer for loosely played recordings.",
                  ),
                ],
              ],
            },
            {
              kind: "p",
              text: l(
                "卡片下半部分回显 已选择的轨道: 与一个个 轨道 N 标签；一个都没选时显示 无轨道 与「请在右侧选择轨道后添加。」。这一行是判断「我到底选上没有」最快的地方。",
                "The lower half of the card echoes 已选择的轨道: / Selected tracks: with one 轨道 N / Track N tag per selection; with nothing selected it shows 无轨道 / No tracks and 请在右侧选择轨道后添加。 / Select tracks on the right and add them. It is the quickest way to confirm your selection actually took.",
              ),
            },
          ],
        },
        {
          id: "tab-midi-tracks",
          title: l("03 轨道选择", "03 Track Selection"),
          blocks: [
            {
              kind: "p",
              text: l(
                "还没扫描时这里显示 暂无轨道信息 与「请先在左侧扫描 MIDI 文件以查看轨道信息。」。扫描之后变成一张表格，列依次是 选择 / 轨道 / 通道 / 乐器 / 音符数。",
                "Before scanning this card shows 暂无轨道信息 / No track info yet and 请先在左侧扫描 MIDI 文件以查看轨道信息。 / Scan the MIDI file on the left to view track info. After scanning it becomes a table with 选择 / Select / 轨道 / Track / 通道 / Channel / 乐器 / Instrument / 音符数 / Notes columns.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "选择 列是复选框，勾选只是**暂存**；点 ✅ 添加选中的轨道 才会写进配置。点完控制台会出现「轨道添加成功」。",
                  "The 选择 / Select column is a checkbox list, but ticking is only **staging** — nothing is committed until you click ✅ 添加选中的轨道 / ✅ Add Selected Tracks, after which the console prints 轨道添加成功 / Tracks added successfully.",
                ),
                l(
                  "乐器 列来自 MIDI 里的乐器名，显示不下会被截断；音符数 是判断「这条是不是主旋律」最实用的依据。",
                  "The 乐器 / Instrument column comes from the MIDI instrument name and is truncated when long; 音符数 / Notes is the most practical clue for spotting the melody track.",
                ),
                l(
                  "轨道很多时表格内可以滚动（最多显示约 350 像素高），勾选状态在滚动中保留。",
                  "With many tracks the table scrolls internally (about 350 px tall) and your ticks survive scrolling.",
                ),
                l(
                  "✂️ 修剪 MIDI 音符 把已选轨道的音符另存为原目录下的 原文件名_trimmed.mid，**不改动原文件**。没选文件报「错误：请先选择 MIDI 文件」，没选轨道报「错误：请先选择至少一个轨道」，成功时报「修剪成功！输出文件：…」。它的用途是把多轨工程瘦身成单轨再喂给软件。",
                  "✂️ 修剪 MIDI 音符 / ✂️ Trim MIDI Notes saves the selected tracks' notes as <_name>_trimmed.mid beside the original, **without touching the original file**. With no file it reports 错误：请先选择 MIDI 文件 / Error: please select a MIDI file first; with no tracks, 错误：请先选择至少一个轨道 / Error: please select at least one track; on success, 修剪成功！输出文件：… / Trim successful! Output file: … Use it to slim a multi-track project down to one track before feeding it in.",
                ),
              ],
            },
            {
              kind: "callout",
              tone: "warn",
              title: l("轨道选错的典型症状", "What a wrong track looks like"),
              text: l(
                "选了鼓轨或铺底伴奏，控制台会以「[ERROR] ❌ 未能找到有效的 recorder，池子为空！」收场——那些音符的时值、音域根本不落在古筝上。看到它就回这一屏换轨道。",
                "Pick a drum track or a backing pad and the run ends with [ERROR] ❌ 未能找到有效的 recorder，池子为空！ / [ERROR] ❌ No valid recorder found, the pool is empty! — those notes simply do not land on a guzheng. When you see it, come back here and change the track.",
              ),
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "tab-technique",
      title: l("技法编辑页", "The Technique tab"),
      image: { image: 2 },
      blocks: [
        {
          kind: "p",
          text: l(
            "技法编辑 页签是**跑完之后的校对工具**，不参与生成。它把 🎵 1. 解析乐曲 产出的 notes_with_techniques 中间文件摊开给你看：识别出了哪些摇指、哪些琶音，哪几条你不认同，可以标出来一次性转成普通音符。",
            "The 技法编辑 / Technique tab is a **post-run review tool** and takes no part in generating. It opens the notes_with_techniques file produced by 🎵 1. 解析乐曲 / 🎵 1. Parse Music and shows you what was detected: which tremolos, which arpeggios — and lets you mark the ones you disagree with and convert them back to ordinary notes in one go.",
          ),
        },
        {
          kind: "note",
          text: l(
            "这一屏只有一张卡片：截图拍到的是片段列表的上半部分，下半部分就是同一张卡片里继续往下滚的更多片段。",
            "This screen is a single card: the capture stops partway down the passage list, and below it are simply more passages in the same list.",
          ),
        },
      ],
      subs: [
        {
          id: "tab-technique-file",
          title: l(
            "00 技法编辑器：文件与加载",
            "00 Technique Editor: file and loading",
          ),
          blocks: [
            {
              kind: "ul",
              items: [
                l(
                  "卡片副标题写着它的用途：「查看和修改摇指与琶音技法片段」。",
                  "The card subtitle states its purpose: 查看和修改摇指与琶音技法片段 / View and edit tremolo and arpeggio passages.",
                ),
                l(
                  "文件: 一行显示当前读取的中间文件路径，形如 output/temp/notes_with_techniques_<MIDI名>_<轨道>.json。切到这个页签时程序**自动**按当前 MIDI 与轨道拼出这个路径并加载，不需要你手动指定。",
                  "The 文件: / File: line shows the intermediate file being read, e.g. output/temp/notes_with_techniques_<midi>_<tracks>.json. The app builds that path from the current MIDI and tracks and loads it **automatically** when you open the tab — no manual pointing.",
                ),
                l(
                  "还没跑过步骤 1 时，这里显示 未加载 并给一条提示横幅「请先在「生成动画」面板执行音符处理以生成技法数据」；文件存在但读不动时换成警告横幅「无法加载 notes_with_techniques 文件，请先在「生成动画」面板完成音符处理」。两种情况的解法一样：去 生成动画 点 🎵 1. 解析乐曲。",
                  "Before step 1 has ever run, the line reads 未加载 / Not loaded with an info banner 请先在「生成动画」面板执行音符处理以生成技法数据 / Run note processing in the Generate Animation panel to produce technique data; if the file exists but cannot be read, a warning banner appears instead: 无法加载 notes_with_techniques 文件，请先在「生成动画」面板完成音符处理 / Cannot load the notes_with_techniques file, please finish note processing in the Generate Animation panel. Both are fixed the same way: run 🎵 1. 解析乐曲 / 🎵 1. Parse Music on 生成动画 / Animation.",
                ),
              ],
            },
          ],
        },
        {
          id: "tab-technique-filter",
          title: l("搜索与过滤栏", "The search and filter bar"),
          blocks: [
            {
              kind: "table",
              head: [
                l("控件", "Control"),
                l("管什么", "What it does"),
                l("取值", "Values"),
                l("默认", "Default"),
              ],
              rows: [
                [
                  l("显示:", "显示: / Show:"),
                  l(
                    "按类型过滤片段列表。全部技法 全列、仅摇指 只看摇指、仅琶音 只看琶音。改完要按 🔍 搜索 才重算列表。",
                    "Filters the passage list by type: 全部技法 / All techniques shows everything, 仅摇指 / Tremolo only only tremolos, 仅琶音 / Arpeggio only only arpeggios. Press 🔍 搜索 / 🔍 Search to recompute the list after changing it.",
                  ),
                  l("三种档位", "Three options"),
                  l("全部技法", "全部技法 / All techniques"),
                ],
                [
                  l("帧:", "帧: / Frame:"),
                  l(
                    "按帧号定位片段：留空表示全部；填了数字（例如 1000）就只留下起止帧落在搜索范围内的片段。输入框的灰色提示是「留空=全部」。",
                    "Locates passages by frame: blank means all; with a number (say 1000) only passages whose start or end frame falls inside the range remain. The grey placeholder reads 留空=全部 / blank=all.",
                  ),
                  l("数字，可留空", "Number, may be blank"),
                  l("留空", "Blank"),
                ],
                [
                  l("± 与 帧 数值框", "± and the 帧 / Frame value box"),
                  l(
                    "搜索半径，即帧号前后各放宽多少帧。0 表示必须正好命中；范围上限 200 帧，拖动步进 0.5。",
                    "The search radius — how many frames either side of the target still match. 0 requires an exact hit; the range tops out at 200 frames with a drag step of 0.5.",
                  ),
                  l("0 ~ 200 帧", "0 – 200 frames"),
                  l("10.0", "10.0"),
                ],
                [
                  l("🔍 搜索", "🔍 搜索 / 🔍 Search"),
                  l(
                    "按上面的条件重新筛一遍列表，并在 共: / 匹配: 一行更新数量。日志会打印「搜索完成，N 个片段匹配」。",
                    "Re-applies the filters and updates the count on the 共: / Total: / 匹配: / Matched: line. The log prints 搜索完成，N 个片段匹配 / Search complete, N passages matched.",
                  ),
                  l("按钮", "Button"),
                  l("—", "—"),
                ],
              ],
            },
            {
              kind: "p",
              text: l(
                "第二行是统计与操作：共: 显示总数（搜索过之后变成 匹配: 加 x/y 个片段），有标记时旁边会出现黄色的 待转换: N 个。右侧两个按钮是 🔄 刷新（重新从文件加载并放弃全部标记）与 💾 保存修改（把标记的片段写回文件，没有标记时是禁用状态）。",
                "The second row is counts and actions: 共: / Total: shows the total (after a search it becomes 匹配: / Matched: with x/y 个片段 / x/y passages), and a yellow 待转换: N 个 / Pending: N appears beside it once anything is marked. The two buttons on the right are 🔄 刷新 / 🔄 Refresh (reload from file, discarding all marks) and 💾 保存修改 / 💾 Save Changes (write the marked passages back; disabled while nothing is marked).",
              ),
            },
            {
              kind: "callout",
              tone: "warn",
              title: l(
                "「保存修改」是不可逆的",
                "“Save Changes” cannot be undone",
              ),
              text: l(
                "点 💾 保存修改 会弹出 确认保存 窗口，写明「确定将标记的 N 个技法片段转为普通音符？」和红字「该操作不可逆！」。确认后程序**就地覆盖** output/temp/ 里那份中间文件——想恢复只能重跑 🎵 1. 解析乐曲。它改的是程序产物，不会碰你的 MIDI 原文件。",
                "Clicking 💾 保存修改 / 💾 Save Changes opens a 确认保存 / Confirm Save window stating 确定将标记的 N 个技法片段转为普通音符？ / Convert the N marked passages to normal notes? with the red warning 该操作不可逆！ / This action is irreversible! Confirming **overwrites in place** the intermediate file in output/temp/ — the only way back is re-running 🎵 1. 解析乐曲 / 🎵 1. Parse Music. It rewrites the app's own product and never touches your MIDI.",
              ),
            },
          ],
        },
        {
          id: "tab-technique-passages",
          title: l("片段卡片列表", "The passage list"),
          blocks: [
            {
              kind: "p",
              text: l(
                "每个片段一张卡片，从上到下：类型徽章、帧范围、规模统计、音符序列，右端一个按钮。数量多了以后列表内部滚动（最高约 600 像素），超过 100 条只显示前 100 条，并在上方提示「匹配 N 个片段，仅显示前 100 个」。",
                "Each passage gets a card: type badge, frame range, size statistic and note sequence, with one button on the right. The list scrolls internally (about 600 px tall); beyond 100 entries only the first 100 are shown, with 匹配 N 个片段，仅显示前 100 个 / N passages match, only the first 100 are shown above.",
              ),
            },
            {
              kind: "table",
              head: [l("卡片元素", "Card element"), l("含义", "Meaning")],
              rows: [
                [
                  l("🔄 摇指 #N", "🔄 摇指 #N / 🔄 Tremolo #N"),
                  l(
                    "识别为摇指的片段，#N 是片段编号。后面的统计显示 ⏱ N 次，即这段里重复拨弦了多少次。",
                    "A passage detected as a tremolo; #N is its passage id. The statistic reads ⏱ N 次 / ⏱ N times — how many repeated plucks the passage contains.",
                  ),
                ],
                [
                  l(
                    "上行 琶音 #N / 下行 琶音 #N",
                    "上行 琶音 #N / Ascending Arpeggio #N / 下行 琶音 #N / Descending Arpeggio #N",
                  ),
                  l(
                    "识别为琶音的片段，方向标明是顺着弦往上还是往下扫。统计显示 🎵 N 个音符。",
                    "A passage detected as an arpeggio, with its sweep direction across the strings. The statistic reads 🎵 N 个音符 / 🎵 N notes.",
                  ),
                ],
                [
                  l(
                    "帧 127.5 → 135.2",
                    "帧 127.5 → 135.2 / Frame 127.5 → 135.2",
                  ),
                  l(
                    "片段覆盖的帧区间；起止帧相同的话只显示一个 帧 N。拿它去对照动画时间线定位。",
                    "The frame range the passage covers; when start and end coincide it collapses to 帧 N / Frame N. Use it to locate the passage on the animation timeline.",
                  ),
                ],
                [
                  l("72 → 74 → 79 → 81", "72 → 74 → 79 → 81"),
                  l(
                    "片段里依次经过的音符（已去重）。正是这一串决定它听起来是扫弦还是逐音。",
                    "The notes the passage passes through, de-duplicated. This sequence is exactly what makes it sound swept rather than picked.",
                  ),
                ],
                [
                  l("📌 标记转换", "📌 标记转换 / 📌 Mark for conversion"),
                  l(
                    "把这条标成「等一下要转成普通音符」。标记后卡片描边变黄、底色变灰，右上角出现 [待转换] 标签，按钮变成 ↩ 撤销标记。标记只是暂存，真正写文件要点 💾 保存修改 并确认。",
                    "Marks this passage for conversion to ordinary notes. A marked card gets a yellow outline, grey fill, a [待转换] / [pending] tag and an ↩ 撤销标记 / ↩ Unmark button. Marking only stages the change — writing it needs 💾 保存修改 / 💾 Save Changes plus confirmation.",
                  ),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "tip",
              title: l("典型用法", "The usual workflow"),
              text: l(
                "先 显示: 仅摇指 按 🔍 搜索，把误判成摇指的段落逐条 📌 标记转换；切到 仅琶音 再筛一遍；然后一次 💾 保存修改。改完必须重跑 🎼 2. 生成指法 往后才生效——指法读的就是这份文件。",
                "Set 显示: 仅摇指 / Show: Tremolo only, press 🔍 搜索 / 🔍 Search and 📌 标记转换 / 📌 Mark for conversion the false tremolos one by one; switch to 仅琶音 / Arpeggio only and repeat; then 💾 保存修改 / 💾 Save Changes once. You must then re-run 🎼 2. 生成指法 / 🎼 2. Generate Fingering for the change to take effect — the fingering stage reads this very file.",
              ),
            },
            {
              kind: "note",
              text: l(
                "一条片段都没有时显示 没有技法片段 与「当前乐曲中没有检测到摇指或琶音技法」；搜出空结果时显示 没有匹配结果 与「请调整搜索条件」。前者不是故障，多半是 参数设置 里 启用摇指检测 / 启用琶音检测 没勾，或两首曲子的技法阈值需要放宽。",
                "With no passages at all the card shows 没有技法片段 / No technique passages and 当前乐曲中没有检测到摇指或琶音技法 / No tremolo or arpeggio techniques detected in the current piece; an empty search shows 没有匹配结果 / No matching results and 请调整搜索条件 / Please adjust the search criteria. The first is not a failure — usually 启用摇指检测 / Enable tremolo detection / 启用琶音检测 / Enable arpeggio detection are unticked on 参数设置 / Parameters, or the two thresholds need loosening.",
              ),
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "tab-execute",
      title: l("生成动画页", "The Animation tab"),
      image: { image: 3 },
      blocks: [
        {
          kind: "p",
          text: l(
            "生成动画 页签是主流程的第 3 步，也几乎是全流程唯一「按一下然后等」的地方。左栏是 01 配置概览 与 03 执行操作，右栏是 04 控制台输出。",
            "The 生成动画 / Animation tab is step 3 and almost the only place where the whole job is “press a button and wait”. The left column holds 01 配置概览 / Configuration Overview and 03 执行操作 / Operations; the right column holds 04 控制台输出 / Console Output.",
          ),
        },
        {
          kind: "note",
          text: l(
            "这一屏没有任何参数需要调——它是执行与观察的地方。要调参数回 参数设置，要换曲子回 MIDI 信息。",
            "There is nothing to tune on this screen: it is where you run and watch. Parameters live on 参数设置 / Parameters; changing the piece means going back to MIDI 信息 / MIDI.",
          ),
        },
      ],
      subs: [
        {
          id: "tab-execute-overview",
          title: l("01 配置概览", "01 Configuration Overview"),
          blocks: [
            {
              kind: "p",
              text: l(
                "生成之前先看这里。它把「等一下会用到什么」原样回显一遍，是发现「选错了」的最后一道关口。",
                "Look here before generating. It echoes exactly what the next run will use, and it is the last chance to notice that something is wrong.",
              ),
            },
            {
              kind: "table",
              head: [
                l("条目", "Row"),
                l("内容", "Content"),
                l("为空 / 异常时", "When empty or wrong"),
              ],
              rows: [
                [
                  l("当前演奏者", "当前演奏者 / Current performer"),
                  l(
                    "所选演奏者的文件名（去掉扩展名），例如 钟离_unreal。它决定输出文件名里开头的演奏者名。",
                    "The selected performer's file name without extension, e.g. 钟离_unreal. It becomes the leading part of every output file name.",
                  ),
                  l(
                    "显示 未选择演奏者 与「请先在参数设置页选择演奏者文件。」",
                    "Shows 未选择演奏者 / No performer selected and 请先在参数设置页选择演奏者文件。 / Please select a performer file on the parameter page first.",
                  ),
                ],
                [
                  l("当前 MIDI", "当前 MIDI / Current MIDI"),
                  l(
                    "MIDI 文件名与它的完整路径，例如 牵丝戏_processed 加文件位置。",
                    "The MIDI file name plus its full path, e.g. 牵丝戏_processed and where it lives.",
                  ),
                  l(
                    "显示 未选择 MIDI 文件 与「请先在 MIDI 处理页完成文件选择与轨道筛选。」",
                    "Shows 未选择 MIDI 文件 / No MIDI file selected and 请先在 MIDI 处理页完成文件选择与轨道筛选。 / Please finish file selection and track filtering on the MIDI page first.",
                  ),
                ],
                [
                  l("轨道", "轨道 / Track"),
                  l(
                    "已添加的轨道编号列表，例如 [1]。这一行的内容与 MIDI 信息 里的 已选择的轨道: 一致。",
                    "The list of added track indices, e.g. [1]. It matches 已选择的轨道: / Selected tracks: on MIDI 信息 / MIDI.",
                  ),
                  l(
                    "显示 [] 说明一个轨道都没加进去。",
                    "Showing [] means no track was ever added.",
                  ),
                ],
                [
                  l("通道", "通道 / Channel"),
                  l(
                    "使用通道 (-1=所有): 的当前值。",
                    "The current value of 使用通道 (-1=所有): / Use channel (-1=all):.",
                  ),
                  l("—", "—"),
                ],
                [
                  l("帧率", "帧率 / FPS"),
                  l(
                    "FPS: 的当前值，输出动画就按这个帧率生成。",
                    "The current value of FPS:; the animation is generated at exactly this rate.",
                  ),
                  l("—", "—"),
                ],
              ],
            },
          ],
        },
        {
          id: "tab-execute-run",
          title: l("03 执行操作", "03 Operations"),
          blocks: [
            {
              kind: "p",
              text: l(
                "卡片里分两组：分步执行 与 快速执行。跑的顺序永远是**解析乐曲 → 生成指法 → 生成动画**，因为每一步都要读上一步写出的中间文件。",
                "The card splits into 分步执行 / Step by Step and 快速执行 / Quick Run. The order is always **parse music → generate fingering → generate animation**, because each stage reads what the previous stage wrote.",
              ),
            },
            {
              kind: "table",
              head: [
                l("按钮", "Button"),
                l("点下去会发生什么", "What happens"),
                l("前置条件", "Prerequisite"),
              ],
              rows: [
                [
                  l("🎵 1. 解析乐曲", "🎵 1. 解析乐曲 / 🎵 1. Parse Music"),
                  l(
                    "解析 MIDI、检测调式、清洗音符并识别摇指 / 琶音。日志会走「阶段 1 初始化 → 阶段 2 MIDI 预处理 → 阶段 3 音符处理」，结束时打印「解析乐曲完成」。产物写在 output/temp/：调式信息与带技法的音符文件。",
                    "Parses the MIDI, detects the tuning, cleans the notes and recognises tremolo / arpeggio. The log walks 阶段 1 / Stage 1 initialization → 阶段 2 / Stage 2 MIDI preprocessing → 阶段 3 / Stage 3 note processing and ends with 解析乐曲完成 / Music parsing complete. Outputs land in output/temp/: the tuning info and the technique-tagged note file.",
                  ),
                  l("已选演奏者与 MIDI 文件", "A performer and a MIDI file"),
                ],
                [
                  l(
                    "🎼 2. 生成指法",
                    "🎼 2. 生成指法 / 🎼 2. Generate Fingering",
                  ),
                  l(
                    "做弦映射（哪个音落在哪根弦）与束搜索（哪根手指怎么弹最顺），走「阶段 4 弦映射 → 阶段 5 生成指法」，进度会刷「指法生成进度：(x/y) - 速度：N steps/s」，最后给出「总熵值」。产物写在 output/recorder/。",
                    "Runs the string mapping (which note lands on which string) and the beam search (which finger plays it most smoothly): 阶段 4 弦映射 / Stage 4 string mapping → 阶段 5 生成指法 / Stage 5 generating fingering, with 指法生成进度：(x/y) - 速度：N steps/s / Fingering progress: (x/y) - speed: N steps/s and a final 总熵值 / Total entropy. Output goes to output/recorder/.",
                  ),
                  l(
                    "步骤 1 已跑过，且 MIDI / 通道 / 轨道没再改过",
                    "Step 1 has run, and MIDI / channel / tracks are unchanged since",
                  ),
                ],
                [
                  l(
                    "🎹 3. 生成动画",
                    "🎹 3. 生成动画 / 🎹 3. Generate Animation",
                  ),
                  l(
                    "读取指法记录，按 参数设置 里的时间参数生成关键帧，写出 _config.zhengdrift 与三个动画文件，日志以「动画生成完成：<路径>」结束。",
                    "Reads the fingering records, generates keyframes using the timing parameters from 参数设置 / Parameters, and writes _config.zhengdrift plus the three animation files, ending with 动画生成完成：<path> / Animation generated: <path>.",
                  ),
                  l(
                    "步骤 2 已跑过，且演奏者没换过",
                    "Step 2 has run, and the performer is unchanged",
                  ),
                ],
                [
                  l("⚡ 一键执行", "⚡ 一键执行 / ⚡ Run All"),
                  l(
                    "把上面三步一次串完，日志按「【步骤 1/3】…【步骤 2/3】…【步骤 3/3】…」分段，最终以「✅ 一键执行（三步流程）完成」收尾。助手提示写着「一键串联全部步骤，适合配置已经确认后的快速产出。」",
                    "Chains all three stages, with the log split as 【步骤 1/3】 / [Step 1/3] … 【步骤 2/3】 / [Step 2/3] … 【步骤 3/3】 / [Step 3/3] … and finishing on ✅ 一键执行（三步流程）完成 / ✅ All steps (three-step flow) complete. Its own hint reads 一键串联全部步骤，适合配置已经确认后的快速产出。 / Run all steps in one go, suitable for fast output after the config is confirmed.",
                  ),
                  l(
                    "已选演奏者与 MIDI、已添加轨道",
                    "A performer, a MIDI file and at least one added track",
                  ),
                ],
              ],
            },
            {
              kind: "p",
              text: l(
                "执行过程中卡片底部出现转圈动画、正在执行... 字样和一个 ❌ 取消 按钮；点它会停止后续步骤并往日志里写「执行已取消」。注意取消只中止流程，已经写出的中间文件会留着。",
                "While a run is in progress the bottom of the card shows a spinner, 正在执行... / Running... and a ❌ 取消 / ❌ Cancel button; cancelling stops the remaining stages and logs 执行已取消 / Execution cancelled. Cancelling only aborts the flow — intermediate files already written stay on disk.",
              ),
            },
            {
              kind: "callout",
              tone: "warn",
              title: l("顺序不能颠倒", "The order is not negotiable"),
              text: l(
                "跳过步骤 1 直接点步骤 2，会得到「错误：找不到调式信息文件，请先执行「解析乐曲」步骤」；跳过步骤 2 直接点步骤 3，会得到「错误：找不到指法文件 output/recorder/…json」并附一句「提示：请先生成指法，或确保 MIDI 文件和音轨选择与之前一致」。中间文件名里带着 MIDI 名与轨道编号，所以中途换了 MIDI 或轨道，必须从步骤 1 重来。",
                'Jumping straight to step 2 yields 错误：找不到调式信息文件，请先执行「解析乐曲」步骤 / Error: tuning info file not found, please run "Parse Music" first; jumping to step 3 yields 错误：找不到指法文件 output/recorder/…json / Error: fingering file not found output/recorder/…json plus 提示：请先生成指法，或确保 MIDI 文件和音轨选择与之前一致 / Hint: generate fingering first, or make sure the MIDI file and track selection match. The intermediate names embed the MIDI name and track indices, so after changing either you must restart from step 1.',
              ),
            },
          ],
        },
        {
          id: "tab-execute-console",
          title: l("04 控制台输出", "04 Console Output"),
          blocks: [
            {
              kind: "p",
              text: l(
                "右栏是所有信息的落点，也是判断成败的地方。顶部一排三个按钮，下面两枚徽章，再下面是日志区。",
                "The right column is where all the information lands and where you judge success. Three buttons on top, two badges below them, and the log area underneath.",
              ),
            },
            {
              kind: "table",
              head: [
                l("控件", "Control"),
                l("点下去会发生什么", "What it does"),
              ],
              rows: [
                [
                  l("复制结果", "复制结果 / Copy Result"),
                  l(
                    "把最近一次生成的 _config.zhengdrift **绝对路径**拷进剪贴板，并写一条「结果路径已复制到剪贴板：…」。还没生成过任何结果时会提示「尚未找到可复制的结果路径」。这一按就是拿来粘贴到插件里的。",
                    "Copies the **absolute path** of the most recent _config.zhengdrift to the clipboard and logs 结果路径已复制到剪贴板：… / Result path copied to clipboard: … Before any successful run it reports 尚未找到可复制的结果路径 / No result path available to copy. This is the path you paste into the plugin.",
                  ),
                ],
                [
                  l("复制全部", "复制全部 / Copy All"),
                  l(
                    "把整份日志拷进剪贴板（无内容时提示「控制台无内容可复制」），排查问题时贴给别人最省事。",
                    "Copies the whole log to the clipboard (控制台无内容可复制 / Console has no content to copy when empty) — the easiest way to hand a failure to someone else.",
                  ),
                ],
                [
                  l("清空信息", "清空信息 / Clear"),
                  l(
                    "清掉日志区。注意 复制结果 记住的路径不会被清掉，仍然可以复制。",
                    "Clears the log area. The path remembered by 复制结果 / Copy Result survives, so it can still be copied.",
                  ),
                ],
                [
                  l("日志 N 条 徽章", "日志 N 条 / N logs badge"),
                  l(
                    "当前日志行数。数字在跑的过程中一直涨，说明程序还活着。",
                    "The current number of log lines. While it keeps climbing, the app is still working.",
                  ),
                ],
                [
                  l(
                    "状态：运行中 / 状态：空闲 徽章",
                    "状态：运行中 / Status: running / 状态：空闲 / Status: idle badge",
                  ),
                  l(
                    "运行状态。跑完（或出错中止）会回到 状态：空闲，这是判断「可以看结果了」最直接的依据。",
                    "The run state. It returns to 状态：空闲 / Status: idle when the run finishes (or aborts on error) — the most direct signal that you can look at the result.",
                  ),
                ],
              ],
            },
            {
              kind: "p",
              text: l(
                "日志有颜色区分：绿色是成功（✓ 弦映射完成、✓ 动画生成完成：… 之类），红色是错误，黄色是警告，灰色等宽字是普通信息。空日志时日志区显示 暂无日志输出，并且会自动滚到最新一行。",
                "The log is colour-coded: green for success (✓ 弦映射完成 / ✓ String mapping complete, ✓ 动画生成完成 / ✓ Animation generated: …), red for errors, yellow for warnings, and monospace grey for plain information. An empty log shows 暂无日志输出 / No logs yet, and the view sticks to the newest line.",
              ),
            },
            {
              kind: "p",
              text: l(
                "几条最值得留意的日志：「检测到最优调式：…（x/y 音符匹配，五声音阶匹配率：z%）」说明调式判定；「速度剔除：移除 N 条来不及到达的演奏记录」是 移动速度 (弦/秒): 不够用的直接信号；「技法伴奏重收集完成：共丢弃 N 个无法演奏的伴奏音符」和「技法过滤：移除了 N 个无法演奏的 Normal 音符」说明有多少音被剪掉；「总熵值：x」可以拿来对比两次调参哪次更顺（越小越好）。",
                "A few log lines are worth watching: 检测到最优调式：…（x/y 音符匹配，五声音阶匹配率：z%） / Optimal tuning detected: … (x/y notes match, pentatonic match rate: z%) reports the tuning decision; 速度剔除：移除 N 条来不及到达的演奏记录 / Speed culling: removed N unreachable performance records is the direct symptom of 移动速度 (弦/秒): / Move speed (strings/s): being too low; 技法伴奏重收集完成：共丢弃 N 个无法演奏的伴奏音符 / Technique accompaniment recollected: N unplayable accompaniment notes dropped and 技法过滤：移除了 N 个无法演奏的 Normal 音符 / Technique filter: removed N unplayable Normal notes say how many notes were trimmed; and 总熵值：x / Total entropy: x lets you compare two parameter runs (lower is smoother).",
              ),
            },
            {
              kind: "callout",
              tone: "tip",
              title: l("跑完先做这一件事", "Do this first when a run ends"),
              text: l(
                "看到绿色的「动画生成完成：<路径>」之后，点一次 复制结果。那一串路径就是插件的入口，省得回 output/ 里翻文件名。",
                "Once the green 动画生成完成：<路径> / Animation generated: <path> line appears, click 复制结果 / Copy Result once. That path is the plugin's entry point, and it saves digging through output/ for the file name.",
              ),
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "defaults",
      title: l("哪些参数可以不动", "Which parameters can stay put"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "下面这张表把四个页签上的控件按「第一次要不要碰」分三档。默认值就是能跑通、也看得过去的组合。",
            "The table below sorts every control across the four tabs into three buckets by whether a first run needs to touch it. The defaults are a combination that both runs and looks reasonable.",
          ),
        },
        {
          kind: "table",
          head: [
            l("档位", "Bucket"),
            l("控件", "Controls"),
            l("说明", "Notes"),
          ],
          rows: [
            [
              l("**完全不用碰**", "**Leave alone**"),
              l(
                "按下耗时 (秒):、抬起耗时 (秒):、最大过渡时长 (秒):、抬掌幅度:、外拨概率 (0-1):、朝向敏感度:、手位变化阈值:、使用通道 (-1=所有):、音符量化:、摇指检测最大间隔值 (秒):、琶音检测最大间隔值 (秒):、启用摇指检测、启用琶音检测",
                "按下耗时 (秒): / Press duration (sec):, 抬起耗时 (秒): / Lift duration (sec):, 最大过渡时长 (秒): / Max transition duration (sec):, 抬掌幅度: / Hand lift amplitude:, 外拨概率 (0-1): / Outward pluck:, 朝向敏感度: / Orientation sensitivity:, 手位变化阈值: / Hand position threshold:, 使用通道 (-1=所有): / Use channel (-1=all):, 音符量化: / Note quantization:, 摇指检测最大间隔值 (秒): / Tremolo max interval (sec):, 琶音检测最大间隔值 (秒): / Arpeggio max interval (sec):, 启用摇指检测 / Enable tremolo detection, 启用琶音检测 / Enable arpeggio detection",
              ),
              l(
                "这些是精修项，默认值覆盖绝大多数曲目。先跑通、看过成片，再决定要不要动其中某一个。",
                "These are refinement controls whose defaults cover the vast majority of pieces. Get a run done and watch the result before considering any of them.",
              ),
            ],
            [
              l("**第一次要选/要填**", "**Must be set on a first run**"),
              l(
                "01 角色选择 下拉菜单、📂 选择 MIDI 文件（或拖放）、🔍 扫描 MIDI 信息、轨道勾选加 ✅ 添加选中的轨道、⚡ 一键执行",
                "The 01 角色选择 / Avatar Selection dropdown, 📂 选择 MIDI 文件 / 📂 Select MIDI File (or dragging one in), 🔍 扫描 MIDI 信息 / 🔍 Scan MIDI Info, ticking tracks plus ✅ 添加选中的轨道 / ✅ Add Selected Tracks, and ⚡ 一键执行 / ⚡ Run All",
              ),
              l(
                "这不是参数，是流程本身。缺任何一项，生成都会以一条明确报错停下（报错原文见 生成动画 页的 04 控制台输出）。",
                "These are not parameters but the flow itself. Miss any one and generation stops with an explicit error (see 04 控制台输出 / Console Output on the 生成动画 / Animation tab).",
              ),
            ],
            [
              l("**大概率会想调**", "**Likely worth tuning**"),
              l(
                "移动速度 (弦/秒):、记录池大小:、FPS:、启用手掌移动速度检查",
                "移动速度 (弦/秒): / Move speed (strings/s):, 记录池大小: / Pool size:, FPS:, 启用手掌移动速度检查 / Enable hand speed check",
              ),
              l(
                "移动速度 (弦/秒): 解决「漏音」；记录池大小: 换「指法更顺但更慢」；FPS: 必须和你的 3D 场景一致；启用手掌移动速度检查 是「宁可漏音也要自然」与「一个音都不能少」的取舍开关。",
                "移动速度 (弦/秒): / Move speed (strings/s): fixes dropped notes; 记录池大小: / Pool size: trades speed for smoother fingering; FPS: must match your 3D scene; and 启用手掌移动速度检查 / Enable hand speed check is the switch between “natural motion even if notes drop” and “not one note may be lost”.",
              ),
            ],
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "faq",
      title: l("参数相关常见问题", "Parameter FAQ"),
      image: { image: 3 },
      blocks: [
        {
          kind: "callout",
          tone: "info",
          text: l(
            "**改了参数要重跑哪一步？** 看改的是哪一类：记录池大小:、移动速度 (弦/秒):、外拨概率 (0-1):、手位变化阈值:、启用手掌移动速度检查 影响指法，要重跑 🎼 2. 生成指法；按下耗时 (秒):、抬起耗时 (秒):、最大过渡时长 (秒):、抬掌幅度:、朝向敏感度: 只影响动画，重跑 🎹 3. 生成动画 即可；两个技法阈值与两个 启用…检测 要在 🎵 1. 解析乐曲 重跑。",
            "**Which step do I re-run after changing a parameter?** It depends on the group: 记录池大小: / Pool size:, 移动速度 (弦/秒): / Move speed (strings/s):, 外拨概率 (0-1): / Outward pluck:, 手位变化阈值: / Hand position threshold: and 启用手掌移动速度检查 / Enable hand speed check feed the fingering, so re-run 🎼 2. 生成指法 / 🎼 2. Generate Fingering; 按下耗时 (秒): / Press duration (sec):, 抬起耗时 (秒): / Lift duration (sec):, 最大过渡时长 (秒): / Max transition duration (sec):, 抬掌幅度: / Hand lift amplitude: and 朝向敏感度: / Orientation sensitivity: only affect the animation, so 🎹 3. 生成动画 / 🎹 3. Generate Animation is enough; the two technique thresholds and the two 启用摇指检测 / Enable tremolo detection and 启用琶音检测 / Enable arpeggio detection switches need 🎵 1. 解析乐曲 / 🎵 1. Parse Music.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          text: l(
            "**记录池大小: 调到 600 值得吗？** 只有在 200 下某几处指法明显别扭时才值得试。它会让 🎼 2. 生成指法 明显变慢（日志里的 steps/s 会掉），跑完对比「总熵值」更有意义——熵值变小说明确实更顺了。",
            "**Is raising 记录池大小: / Pool size: to 600 worth it?** Only when a few passages are visibly awkward at 200. It slows 🎼 2. 生成指法 / 🎼 2. Generate Fingering down noticeably (the steps/s in the log drops), and comparing the resulting 总熵值 / Total entropy is the meaningful test: a lower entropy means the fingering really is smoother.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          text: l(
            "**FPS: 调高会不会更流畅？** 不会凭空变流畅，只会把同一段演奏切成更多帧、文件更大。它必须和 Blender / Unreal 场景的帧率一致，否则播放速度会整体走样。",
            "**Does a higher FPS: make playback smoother?** Not by itself — it only slices the same performance into more frames and a bigger file. It must match the frame rate of your Blender / Unreal scene, or playback speed drifts overall.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          text: l(
            "**按下耗时 (秒): 和 抬起耗时 (秒): 谁大谁小有讲究吗？** 默认抬起（0.12）比按下（0.0875）略慢，手看起来更稳。想更利落就把两者都压小，但低于 0.05 会显得抽动。",
            "**Does it matter whether 按下耗时 (秒): / Press duration (sec): is larger than 抬起耗时 (秒): / Lift duration (sec):?** By default lift (0.12) is slightly slower than press (0.0875), which reads as a steadier hand. For a snappier feel shrink both, but below 0.05 it starts to twitch.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          text: l(
            "**参数会存下来吗？** 不会。语言选择会写进程序目录的 config.json，但参数每次启动都回默认值。调好的一套记得自己记下来，或直接写在曲子的备注里。",
            "**Are parameter values saved?** No. The language choice is written to config.json beside the app, but parameters return to their defaults on every launch. Note down a setup you like, or keep it next to the piece.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          text: l(
            "**可以同时跑两份曲子吗？** 不行。中间文件名只带 MIDI 名与轨道号，同一时刻只有一套配置。想对比两种参数，就改一次、跑一次、把 output/ 里的结果另存到别处再改回来。",
            "**Can two pieces run at once?** No. The intermediate names carry only the MIDI name and track indices, so only one configuration exists at a time. To compare two parameter sets, change, run, save the output/ result elsewhere, then change back.",
          ),
        },
      ],
    },
  ],
};
